import { DeleteOutline } from '@mui/icons-material';
import { IconButton, MenuItem, Select, styled, Typography } from '@mui/material';
import { TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { FeedbackContext } from '../../../constants/feedbackConstants';
import { processQuestion, userEditSave } from '../../../services/dealGPT';
import type { ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { AnalysisQuestionStatus, ProcessReason, UpdatedReason } from '../../../Types/enums';
import { FeedbackInterface } from '../../../Types/feedback';
import { formatDate } from '../../../util/dateUtils';
import { renderMessage } from '../../../util/messageUtils';
import Feedback from '../../Feedback';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useFeatures } from '../../Providers/FeatureProvider';
import { useNotifications } from '../../Providers/NotificationsProvider';
import QuestionStatus from '../QuestionStatus';
import { AugmentedResponseWithSources } from '../SourcesDisplay';
import ModifyTone from './ModifyTone';
import MoreOptionsMenu from './MoreOptionsMenu';
import ReadMoreText from './ReadMoreText';
import ReferenceDocuments from './ReferenceDocuments';
import SectionButtons from './SectionButtons';
import UnprocessedChangesDot from './UnprocessedChangesDot';

const HeaderLabel = styled(Typography)({
  marginBottom: '10px',
  color: '#092B49',
  fontWeight: '700',
});

const ReactMarkdownWrapper = styled('div')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
});

const FlexDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

interface QuestionAnswerProps {
  projectId: string;
  index: number;
  responseObject: ProjectRunAnalysisQuestion;
  versions: ProjectRunAnalysisQuestion[];
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  refetchQuestions: () => void;
  handleDelete: (questionIndex: number) => void;
  suggestSaveOrProcessAll: 'save' | 'process' | null;
  setSuggestSaveOrProcessAll: React.Dispatch<React.SetStateAction<'save' | 'process' | null>>;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
}

const QuestionAnswer: React.FC<QuestionAnswerProps> = ({
  projectId,
  index,
  responseObject,
  versions,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  refetchQuestions,
  handleDelete,
  suggestSaveOrProcessAll,
  setSuggestSaveOrProcessAll,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
}) => {
  const [analysisQuestionData, setAnalysisQuestionData] =
    useState<ProjectRunAnalysisQuestion>(responseObject);
  const [questionVersions, setQuestionVersions] = useState<ProjectRunAnalysisQuestion[]>(versions);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentValues, setCurrentValues] = useState<Record<string, string>>({});
  const [attemptedSaveOrProcessCurrent, setAttemptedSaveOrProcessCurrent] = useState<
    'save' | 'process' | null
  >(null);
  const { refetchNotifications } = useNotifications();

  const userInputName = `QA-userInput-${index}`;
  const responseName = `QA-response-${index}`;
  const analytics = useAnalytics();
  const questionStatuses: string[] = [];

  if (analysisQuestionData.validationResponse) {
    questionStatuses.push('VALIDATED');
  }

  const handleSave = useCallback(
    async (
      values: Record<string, string>,
      reason?: UpdatedReason,
      processReason?: ProcessReason
    ) => {
      try {
        const tempAnalysisQuestion: ProjectRunAnalysisQuestion = { ...analysisQuestionData };

        if (reason) {
          tempAnalysisQuestion.updatedReason = reason;
        }
        tempAnalysisQuestion.userInput = values[userInputName];

        // Adding a new question
        if (tempAnalysisQuestion.status === 'INITIAL') {
          if (responseObject.displayOrder) {
            // Use the displayOrder from the responseObject - being controlled from StepPage when user clicks "Add"
            tempAnalysisQuestion.displayOrder = responseObject.displayOrder;
          }
        }

        if (tempAnalysisQuestion.response) {
          tempAnalysisQuestion.response = values[responseName];
        }

        let updatedQuestion = tempAnalysisQuestion;
        if (reason === UpdatedReason.USER_EDIT || reason === UpdatedReason.USER_INPUT) {
          if (reason === UpdatedReason.USER_INPUT) {
            tempAnalysisQuestion.status = AnalysisQuestionStatus.INPUT_PENDING;
          }

          const updatedResult = await userEditSave(tempAnalysisQuestion);

          if (updatedResult.id) {
            updatedQuestion = updatedResult;
            setAnalysisQuestionData(updatedQuestion);
            enqueueSnackbar(`Question ${index + 1} saved successfully`, { variant: 'success' });

            if (reason === UpdatedReason.USER_EDIT) {
              refetchQuestions();
            }
          } else {
            enqueueSnackbar('Failed to save content. Please try again later.', {
              variant: 'error',
            });
            return;
          }
        }

        if (
          tempAnalysisQuestion.updatedReason === UpdatedReason.REPROCESS ||
          processReason === ProcessReason.APPLY_CHANGES
        ) {
          enqueueSnackbar(`Processing flash question ${index + 1}`, { variant: 'info' });
          setIsLoadingThis(true);
          if (processReason === ProcessReason.PROCESS_THIS) {
            setAttemptedSaveOrProcessCurrent('process');

            setSuggestSaveOrProcessAll('save');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setSuggestSaveOrProcessAll(null);
          } else if (processReason === ProcessReason.APPLY_CHANGES) {
            setIsLoadingApplyChanges(true);
          }
          await processQuestion(updatedQuestion, processReason);
          refetchNotifications();
          setAttemptedSaveOrProcessCurrent(null);
          refetchQuestions();
        }
      } catch (error) {
        enqueueSnackbar('Failed to save content. Please try again later.', {
          variant: 'error',
        });
      }
    },
    [
      analysisQuestionData,
      index,
      refetchQuestions,
      responseName,
      responseObject.displayOrder,
      setIsLoadingThis,
      setIsLoadingApplyChanges,
      setSuggestSaveOrProcessAll,
      userInputName,
      refetchNotifications,
    ]
  );

  const onSubmit = async () => {
    // setIsLoading(true);
    // const tempAnalysisQuestion = await handleSave(values, UpdatedReason.REPROCESS);
    // if (tempAnalysisQuestion) {
    //   enqueueSnackbar('Your content is being processed', { variant: 'success' });
    //   await processQuestion(tempAnalysisQuestion);
    //   refetchQuestions();
    // }
  };

  const handleVersionChange = useCallback(
    async (value: string) => {
      const version = versions.find((v) => v.id === value);
      analytics.event(
        AnalyticsEvent.WM_IA_A_QA_QUESTION_VERSION_CHANGE,
        {
          id: value,
          value: versions[0].pageName,
        },
        {
          pageName: versions[0].pageName,
          oldVersionId: analysisQuestionData.id,
          newVersionId: value,
          scope: versions[0].scope,
          runId: versions[0].runId,
          projectId,
        }
      );
      if (version) {
        setAnalysisQuestionData(version);
      }
    },
    [versions, analytics, projectId, analysisQuestionData.id]
  );

  useEffect(() => {
    setAnalysisQuestionData(responseObject);

    if (!responseObject.response) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [responseObject]);

  useEffect(() => {
    // Handle the parent suggesting we should attempt processing or saving
    if (suggestSaveOrProcessAll && currentValues[userInputName]) {
      const attemptSave = async () => {
        setIsLoadingThis(true);

        // We want the user to be able to process/reprocess as many times as they want
        if (suggestSaveOrProcessAll === 'process' && attemptedSaveOrProcessCurrent !== 'process') {
          setAttemptedSaveOrProcessCurrent('process');
          setTimeout(async () => {
            enqueueSnackbar(`Processing flash question ${index + 1}`, { variant: 'success' });
            const tempAnalysisQuestion: ProjectRunAnalysisQuestion = { ...analysisQuestionData };
            tempAnalysisQuestion.updatedReason = UpdatedReason.REPROCESS;
            tempAnalysisQuestion.userInput = currentValues[userInputName];
            tempAnalysisQuestion.response = currentValues[responseName];

            await processQuestion(tempAnalysisQuestion, ProcessReason.PROCESS_THIS);
            setAttemptedSaveOrProcessCurrent(null);
            refetchQuestions();
          }, index * 1500);
        } else if (
          suggestSaveOrProcessAll === 'save' &&
          attemptedSaveOrProcessCurrent === null &&
          currentValues[userInputName] !== null &&
          analysisQuestionData.response === null
        ) {
          setAttemptedSaveOrProcessCurrent('save');
          await handleSave(currentValues, UpdatedReason.USER_INPUT);
        }
      };
      attemptSave();
    }
  }, [
    attemptedSaveOrProcessCurrent,
    currentValues,
    handleSave,
    refetchQuestions,
    setIsLoadingThis,
    suggestSaveOrProcessAll,
    index,
    analysisQuestionData,
    responseName,
    userInputName,
  ]);

  useEffect(() => {
    let activeVersions = versions;

    if (versions.some((version) => version.status === 'ANSWERED')) {
      activeVersions = versions.filter((version) => version.status === 'ANSWERED');
    }
    setQuestionVersions(activeVersions);
    if (activeVersions[0] && (!analysisQuestionData || analysisQuestionData.id === undefined)) {
      setAnalysisQuestionData(activeVersions[0]);
    }
  }, [analysisQuestionData, versions]);

  const initialValues = useMemo(
    () => ({
      [userInputName]: analysisQuestionData.userInput,
      [responseName]: analysisQuestionData.response,
    }),
    [userInputName, responseName, analysisQuestionData.userInput, analysisQuestionData.response]
  );

  const features = useFeatures();
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <FormSpy
            subscription={{ values: true }}
            onChange={(props) => {
              // I don't have a really good reason to be using this eslint-disable, when we get a chance
              // lets do what it takes to not have to use this disable line
              // eslint-disable-next-line react/prop-types
              setCurrentValues(props.values);
            }}
          />
          <FlexDiv>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <HeaderLabel>Enter in Key Question:</HeaderLabel>
                <IconButton
                  onClick={() => handleDelete(index)}
                  sx={{ float: 'right' }}
                  disabled={analysisQuestionData.scope === 'PROJECT'}
                >
                  <DeleteOutline
                    sx={{
                      border: '1px solid #B8C1C7',
                      borderRadius: '4px',
                      padding: '12px',
                      fontSize: '20px',
                      height: '100%',
                    }}
                  />
                </IconButton>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: '700', color: '#092B49', marginRight: '8px' }}>
                  {index + 1})
                </Typography>
                {!analysisQuestionData.response ? (
                  <TextField
                    name={userInputName}
                    multiline
                    disabled={analysisQuestionData.scope === 'PROJECT'}
                  />
                ) : (
                  <Typography>{analysisQuestionData.userInput}</Typography>
                )}
              </div>
              {!responseObject.response && (
                <SectionButtons
                  projectId={projectId}
                  handleSave={handleSave}
                  values={values}
                  hasGenerated={!!analysisQuestionData.response}
                  handleEdit={setIsEditing}
                  isEditing={isEditing}
                  showEditButton={false}
                  showSaveButton={false}
                  allowGeneration={!!values[userInputName]}
                />
              )}
            </div>
            {!!responseObject.response && (
              <FlexDiv>
                <div>
                  <FlexDiv>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '12px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#757575',
                        }}
                      >
                        RESPONSE
                      </Typography>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Select
                          value={
                            questionVersions.some((v) => v.id === analysisQuestionData?.id)
                              ? analysisQuestionData.id
                              : questionVersions[0].id
                          }
                          onChange={(event) => handleVersionChange(event.target.value)}
                          size="small"
                        >
                          {questionVersions.map((version) => (
                            <MenuItem
                              value={version.id}
                              key={version.id}
                              selected={version.id === analysisQuestionData.id}
                            >
                              Generated on {version.createdAt && formatDate(version.createdAt)} |{' '}
                              {version.updatedReason}
                            </MenuItem>
                          ))}
                        </Select>
                        {questionVersions.length > 0 &&
                          questionVersions[0].unprocessedDownstreamChanges && (
                            <UnprocessedChangesDot />
                          )}
                      </div>
                      <ModifyTone
                        projectId={projectId}
                        question={analysisQuestionData}
                        setIsLoading={setIsLoadingThis}
                        refetchQuestions={refetchQuestions}
                      />
                    </div>
                    <div style={{ display: 'flex' }}>
                      {analysisQuestionData.validationResponse && (
                        <QuestionStatus statuses={questionStatuses} />
                      )}
                      <SectionButtons
                        projectId={projectId}
                        handleSave={handleSave}
                        values={values}
                        hasGenerated={!!analysisQuestionData.response}
                        handleEdit={setIsEditing}
                        isEditing={isEditing}
                        question={analysisQuestionData}
                        showGenerateButton={false}
                      />
                      <MoreOptionsMenu
                        projectId={projectId}
                        analysisQuestionData={analysisQuestionData}
                        handleSave={handleSave}
                        values={values}
                        isEditing={isEditing}
                        handleValidateFindingsClick={handleValidateFindingsClick}
                      />
                    </div>
                    {isEditing ? (
                      <TextField name={responseName} multiline />
                    ) : (
                      <ReactMarkdownWrapper>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, supersub]}
                          components={{
                            sup: ({ children }) => (
                              <AugmentedResponseWithSources
                                text={children.toString()}
                                sources={analysisQuestionData.documents ?? []}
                              />
                            ),
                          }}
                        >
                          {renderMessage({
                            message: analysisQuestionData.response || '',
                            sources: analysisQuestionData.documents || [],
                          })}
                        </ReactMarkdown>
                        {analysisQuestionData?.validationResponse && (
                          <div>
                            <hr />
                            <Typography>Validation</Typography>
                            <ReactMarkdown remarkPlugins={[remarkGfm, supersub]}>
                              {analysisQuestionData.validationResponse || ''}
                            </ReactMarkdown>
                          </div>
                        )}
                      </ReactMarkdownWrapper>
                    )}
                  </FlexDiv>
                </div>
                <ReferenceDocuments
                  questionId={analysisQuestionData.id}
                  documents={analysisQuestionData.documents}
                  showAnalysisText={true}
                />
                {analysisQuestionData.validationSources &&
                  analysisQuestionData.validationSources.length > 0 && (
                    <div style={{ marginTop: '1.3em' }}>
                      <ReferenceDocuments
                        questionId={analysisQuestionData.id}
                        documents={analysisQuestionData.validationSources}
                        showReferenceTitle={false}
                        showAnalysisText={true}
                        showValidationTitle={true}
                      />
                    </div>
                  )}

                {features.deal_gpt_enriched_prompt_display && (
                  <>
                    <Typography sx={{ fontWeight: '700', color: '#092B49', marginTop: '20px' }}>
                      Enriched Prompt:
                    </Typography>
                    <div style={{ marginBottom: '15px' }}>
                      <ReadMoreText
                        text={analysisQuestionData.enrichedPrompt || ''}
                        maxLength={120}
                      />
                    </div>
                  </>
                )}
                <Feedback
                  initialFeedback={
                    analysisQuestionData.analysisQuestionFeedback as FeedbackInterface | undefined
                  }
                  context={FeedbackContext.ANALYSIS_QUESTION}
                  onFeedbackSubmit={(feedback) =>
                    handleFeedbackSubmit(feedback, analysisQuestionData.id)
                  }
                />
              </FlexDiv>
            )}
          </FlexDiv>
        </form>
      )}
    />
  );
};

export default QuestionAnswer;
